import { FormatService } from '@/app/common/format/format.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { GenericPostsDto } from '@/app/common/general-types/super-social.dto';
import { ImageService } from '@/app/services/image/image.service';
import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { DecimalPipe, DatePipe } from '@angular/common';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { UltraSocialService, UltraSocialSocialAccount } from '@/app/services/ultra-social/ultra-social';
import { forkJoin, Observable, Subscription, take } from 'rxjs';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { VideoFormatEnum } from '@/app/common/general-types/briefs.dto';
import { MgcPortfolioInlineVideoComponent } from '../mgc-portfolio/mgc-portfolio-inline-video.component';
import { DialogComponent } from '@/app/atoms/dialog/dialog.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';

type UltraYoutubePost = {
  assetImageKeys: string[] | null;
  assetImageUrls: string[] | null;
  assetMediaKey: string | null;
  assetMediaUrl: string | null;
  assetThumbnailKey: string | null;
  assetThumbnailUrl: string | null;
  caption: string | null;
  commentCount: number;
  id: string;
  likeCount: number;
  permalink: string;
  postedAt: Date;
  shareCount: number;
  thumbnailUrl: string | null;
  type: string;
  viewCount: number;
};

type UltraYoutubeAccount = {
  id: string;
  platform: 'tiktok' | 'instagram' | 'youtube' | 'snapchat';
  handle: string;
  displayName: string;
  avatarUrl: string;
  bio: string | null;
  status: string | null;
  followerCount: number;
  followingCount: number;
  likesCount: number;
  postCount: number;
};

@Component({
  selector: 'mgc-youtube',
  imports: [
    LdgImgComponent,
    DecimalPipe,
    VideoThumbnailComponent,
    HasRoleDirectiveDirective,
    DatePipe,
    LoadingInlineComponent,
    MgcPortfolioInlineVideoComponent,
    DialogComponent,
  ],
  templateUrl: './mgc-youtube.html',
  styleUrl: './mgc-youtube.css',
})
export class MgcYoutube implements OnInit, OnDestroy {
  @Input() creator!: CreatorResponseDto;
  @Input() ultra_social_youtube: UltraSocialSocialAccount | undefined;
  @Input() ultra_social_creator_id: string | undefined;

  youtubeBasics = signal<any>(undefined);
  youtubePosts = signal<GenericPostsDto[]>([]);

  refreshYoutubeAccountLoading = signal<boolean>(false);
  accountRefreshed = signal<boolean>(false);
  accountOwnershipChanged = signal<boolean>(false);

  readonly usingUltraSocial = signal(false);
  ultra_youtube_account = signal<UltraYoutubeAccount | undefined>(undefined);
  ultra_youtube_posts = signal<UltraYoutubePost[] | undefined>(undefined);

  readonly embedVideoId = signal<string | undefined>(undefined);
  readonly youtubeEmbedOpen = computed(() => !!this.embedVideoId());
  readonly youtubeEmbedSrc = computed<SafeResourceUrl | null>(() => {
    const id = this.embedVideoId();
    if (!id) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1`,
    );
  });
  readonly youtubeWatchUrl = computed(() => {
    const id = this.embedVideoId();
    return id ? `https://www.youtube.com/watch?v=${id}` : undefined;
  });

  private currentUserService = inject(CurrentUserService);
  private readonly sanitizer = inject(DomSanitizer);
  subscriptionList = new Subscription();

  readonly youtubeHandleRevealed = computed(() => this.currentUserService.subscriptionActive());

  /** Services */
  public superSocialService = inject(SuperSocialService);
  public formatService = inject(FormatService);
  public imageService = inject(ImageService);
  public toastNotificationService = inject(ToastNotificationService);
  public ultraSocialService = inject(UltraSocialService);
  /** Services - END */

  ngOnInit(): void {
    console.log('MGC Youtube: ', this.creator);
    if (this.ultra_social_creator_id && this.ultra_social_youtube) {
      this.usingUltraSocial.set(true);
      this.getUltraYoutubeAccount(this.ultra_social_creator_id, this.ultra_social_youtube.id);
    } else if (this.creator && this.creator.ssCreator && this.creator.ssCreator.youtubeAccountId && this.creator.ssCreator.youtubeAccountId !== 'Ownership Changed') {
      this.getSuperSocialYoutubeAccount(this.creator.ssCreator.youtubeAccountId);
      this.getSuperSocialYoutubePosts(this.creator.ssCreator.youtubeAccountId);
    } else if (this.creator && this.creator.ssCreator && this.creator.ssCreator.youtubeAccountId && this.creator.ssCreator.youtubeAccountId == 'Ownership Changed') {
      this.youtubeBasics.set(undefined);
      this.youtubePosts.set([]);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getSuperSocialYoutubeAccount(youtubeAccountId: string) {
    this.superSocialService.getSuperSocialYoutubeAccount(youtubeAccountId).subscribe((res) => {
      this.youtubeBasics.set(res);
      console.log('youtubeBasics: ', this.youtubeBasics());
    });
  }

  getSuperSocialYoutubePosts(youtubeAccountId: string) {
    this.superSocialService.getYoutubeDisplayContent(youtubeAccountId).subscribe((res) => {
      this.youtubePosts.set(res);
      console.log('youtubePosts: ', this.youtubePosts());
    });
  }

  getUltraYoutubeAccount(ultra_social_creator_id: string, socialAccountId: string) {
    this.subscriptionList.add(
      this.loadData(ultra_social_creator_id, socialAccountId).pipe(take(1)).subscribe((data) => {
        this.ultra_youtube_account.set(data.account);
        this.ultra_youtube_posts.set(data.posts);
      }),
    );
  }

  private loadData(ultra_social_creator_id: string, socialAccountId: string): Observable<{
    account: UltraYoutubeAccount;
    posts: UltraYoutubePost[];
  }> {
    return forkJoin({
      account: this.ultraSocialService.getSocialAccount(ultra_social_creator_id, socialAccountId),
      posts: this.ultraSocialService.getAccountPosts(ultra_social_creator_id, socialAccountId),
    });
  }

  youtubeVideoIdFromSource(source: string | null | undefined): string | undefined {
    if (!source) {
      return undefined;
    }
    const trimmed = source.trim();
    if (/^[\w-]{11}$/.test(trimmed)) {
      return trimmed;
    }
    try {
      const parsed = new URL(trimmed);
      if (parsed.hostname.includes('youtu.be')) {
        const id = parsed.pathname.split('/').filter(Boolean)[0];
        return id && /^[\w-]{11}$/.test(id) ? id : undefined;
      }
      const fromQuery = parsed.searchParams.get('v');
      if (fromQuery && /^[\w-]{11}$/.test(fromQuery)) {
        return fromQuery;
      }
      const fromPath = parsed.pathname.match(/\/(?:shorts|embed|live|v)\/([\w-]{11})/);
      return fromPath?.[1];
    } catch {
      return undefined;
    }
  }

  ultraEmbedVideoId(post: UltraYoutubePost): string | undefined {
    return this.youtubeVideoIdFromSource(post.permalink) ?? this.youtubeVideoIdFromSource(post.id);
  }

  openUltraYoutubeEmbed(post: UltraYoutubePost): void {
    const id = this.ultraEmbedVideoId(post);
    if (id) {
      this.embedVideoId.set(id);
    }
  }

  closeYoutubeEmbed(): void {
    this.embedVideoId.set(undefined);
  }

  hasUltraVideoPreview(post: UltraYoutubePost): boolean {
    return !!(
      post.assetMediaUrl ||
      post.assetThumbnailUrl ||
      post.assetThumbnailKey ||
      this.isUsableMediaUrl(post.thumbnailUrl)
    );
  }

  ultraPostImageSrc(post: UltraYoutubePost): string | undefined {
    if (this.isUsableMediaUrl(post.assetImageUrls?.[0])) {
      return post.assetImageUrls![0];
    }
    if (post.assetImageKeys?.[0]) {
      return this.imageService.getImageSource(post.assetImageKeys[0], 'original');
    }
    return undefined;
  }

  ultraThumbnailUrl(post: UltraYoutubePost): string | undefined {
    const url =
      this.usableUrlOrEmpty(post.assetThumbnailUrl) || this.usableUrlOrEmpty(post.thumbnailUrl);
    return url || undefined;
  }

  ultraThumbnailCdnKey(post: UltraYoutubePost): string | undefined {
    return post.assetThumbnailKey ?? undefined;
  }

  assembledVideoObject(post: UltraYoutubePost): VideoResponseDto {
    const thumbnailUrl =
      this.usableUrlOrEmpty(post.assetThumbnailUrl) ||
      this.usableUrlOrEmpty(post.thumbnailUrl);

    return {
      createdDate: post.postedAt,
      creator: this.creator,
      creatorId: this.creator.blazeFirebaseUid,
      fullUrl: post.assetMediaUrl ?? '',
      id: post.id,
      key: post.assetMediaKey ?? '',
      status: 1,
      thumbnail: thumbnailUrl,
      ugcBriefApplicationId: null,
      videoFormat: VideoFormatEnum.Vertical,
      videoLength: 0,
      videoName: post.caption ?? '',
      briefApplicationId: '',
      thumbnailURL: thumbnailUrl,
      thumbnailCdnKey: post.assetThumbnailKey,
    };
  }

  getUpdatedDateColour(updatedDate: Date): string {
    if (updatedDate) {
      const hoursAgo = Math.floor((new Date().getTime() - new Date(updatedDate).getTime()) / (1000 * 60 * 60));
      if (hoursAgo < 12) {
        return 'text-green-500';
      } else if (hoursAgo < 24) {
        return 'text-primary';
      } else if (hoursAgo < 48) {
        return 'text-amber-500';
      }
      else {
        return 'text-red-500';
      }
    }
    return 'text-gray-500';
  }

  allowedToRefreshYoutubeAccount(updatedDate: Date): boolean {
    if (updatedDate) {
      const hoursAgo = Math.floor((new Date().getTime() - new Date(updatedDate).getTime()) / (1000 * 60 * 60));
      if (hoursAgo < 12) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  refreshYoutubeAccount() {
    console.log('-----> refreshYoutubeAccount');
    this.refreshYoutubeAccountLoading.set(true);

    if (this.creator.ssCreator && this.creator.ssCreator.youtubeAccountId) {
      this.superSocialService.refreshYoutubeAuthentic(this.creator.ssCreator.youtubeAccountId).subscribe((res) => {
        console.log('refreshYoutubeAccount: ', res);
        this.refreshYoutubeAccountLoading.set(false);
        this.accountRefreshed.set(true);
      }, err => {
        console.error('Error refreshing YouTube account: ', err);
        this.refreshYoutubeAccountLoading.set(false);
        void this.toastNotificationService.alert({
          title: 'Error Refreshing YouTube Account!',
          message: 'Something went wrong. If the problem persists, please contact support.',
          type: 'error',
          icon: 'error'
        });
      });
    }
  }

  private isUsableMediaUrl(url: string | null | undefined): url is string {
    if (!url) {
      return false;
    }
    return !url.includes('www.tikwm.com') && !url.includes('ionicframework.com/docs/img/demos/thumbnail.svg');
  }

  private usableUrlOrEmpty(url: string | null | undefined): string {
    return this.isUsableMediaUrl(url) ? url : '';
  }
}
